import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import { SetCancellationPolicyRequest } from "../../organization/contracts/SetCancellationPolicyRequest";
import { toCancellationPolicy } from "../../organization/mappers/CancellationPolicyResponseMapper";
import { CancellationPolicy } from "../../organization/models/CancellationPolicy";
import { SessionCancellationPolicyResponse } from "../contracts/SessionCancellationPolicyResponse";

/** A Session's effective policy and whether it is the Session's own override. */
export interface SessionCancellationPolicy {
  policy: CancellationPolicy;
  isOverride: boolean;
}

const urlOf = (sessionId: string) => `/bookings/${sessionId}/cancellation-policy`;

const toSessionPolicy = (resp: SessionCancellationPolicyResponse): SessionCancellationPolicy => ({
  policy: toCancellationPolicy(resp),
  isOverride: resp.isOverride,
});

export const fetchSessionCancellationPolicy = createAsyncThunk<
  SessionCancellationPolicy,
  string,
  { rejectValue: ApiErrorResponse }
>("sessions/fetchCancellationPolicy", async (sessionId, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<SessionCancellationPolicyResponse>(urlOf(sessionId));
    return toSessionPolicy(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to load the cancellation policy"));
  }
});

/** Gives the Session its own cancellation policy. */
export const saveSessionCancellationPolicy = createAsyncThunk<
  SessionCancellationPolicy,
  { sessionId: string; request: SetCancellationPolicyRequest },
  { rejectValue: ApiErrorResponse }
>("sessions/saveCancellationPolicy", async ({ sessionId, request }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put<SessionCancellationPolicyResponse>(urlOf(sessionId), request);
    return toSessionPolicy(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to save the cancellation policy"));
  }
});

/** Drops the Session's override, so the organization's policy applies again. */
export const clearSessionCancellationPolicy = createAsyncThunk<
  void,
  string,
  { rejectValue: ApiErrorResponse }
>("sessions/clearCancellationPolicy", async (sessionId, { rejectWithValue }) => {
  try {
    await axiosInstance.delete(urlOf(sessionId));
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to reset the cancellation policy"));
  }
});
